"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { alpha, styled, useTheme } from "@mui/material/styles";

import {
  Add,
  Edit,
  Delete,
  Refresh,
  Close,
  Save,
  Search,
  FilterList,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   CUSTOM TABLE SCROLLBAR
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* Scrollable modal bodies use the same scrollbar. */

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const EXPENSE_LIST_API = "/library/expense/list/";
const EXPENSE_CREATE_API = "/library/expense/create/";
const EXPENSE_BULK_CREATE_API = "/library/expense/bulk-create/";
const EXPENSE_UPDATE_API = "/library/expense/update/";
const EXPENSE_DELETE_API = "/library/expense/delete/";
const CATEGORY_LIST_API = "/library/expense-category/list/";
const PAYMENT_METHOD_API = "/master/payment-methods/";   // ADD

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const keys = ["accessToken", "access_token", "token"];

  for (const key of keys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser =
      sessionStorage.getItem("user") || localStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("User parse error:", error);

    return null;
  }
};

/* =========================================================
   RESPONSE HELPERS
========================================================= */

const getArray = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.records)) return data.records;

  if (Array.isArray(data?.expenses)) return data.expenses;

  if (Array.isArray(data?.categories)) return data.categories;

  return [];
};

/* =========================================================
   TODAY DATE
========================================================= */

const getTodayDate = () => {
  const today = new Date();

  const year = today.getFullYear();

  const month = String(today.getMonth() + 1).padStart(2, "0");

  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/* =========================================================
   COMPONENT
========================================================= */

function ExpenseEntry() {
  const theme = useTheme();

  /* Global branch selector — replaces all local branch
     state, branch dropdowns and fetchBranches calls. */

  const { selectedBranchId } = useBranch();

  const isDark = theme.palette.mode === "dark";

  const textPrimary = theme.palette.text.primary;

  const textSecondary = theme.palette.text.primary;

  const borderColor = theme.palette.divider;

  const inputBackground = theme.palette.background.paper;

  /* =======================================================
     USER
  ======================================================= */

  const [currentUser, setCurrentUser] = useState(null);

  const [organizationId, setOrganizationId] = useState("");

  /* =======================================================
     DATA
  ======================================================= */

  const [expenses, setExpenses] = useState([]);

  const [categories, setCategories] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [loadingCategories, setLoadingCategories] = useState(false);

  const [saving, setSaving] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState([]);
const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  /* Shows the table loader from first render until the
     initial fetch completes. */

  const [initialLoading, setInitialLoading] = useState(true);

  /* =======================================================
     SEARCH — client-side (list endpoint has no `search`
     param in the Swagger spec)
  ======================================================= */

  const [searchText, setSearchText] = useState("");

  /* =======================================================
     FILTERS
  ======================================================= */

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [filterCategory, setFilterCategory] = useState("");

  const [filterPaymentMode, setFilterPaymentMode] = useState("");

  /* Per Swagger, the list params are `expense_date_from`
     and `expense_date_to` (NOT date_from / date_to). */

  const [filterDateFrom, setFilterDateFrom] = useState("");

  const [filterDateTo, setFilterDateTo] = useState("");

  /* =======================================================
     PAGINATION (client-side)
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     CREATE / EDIT
  ======================================================= */

  const [openDialog, setOpenDialog] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [selectedExpenseId, setSelectedExpenseId] = useState(null);

  const emptyForm = {
    expense_date: getTodayDate(),
    category: "",
    amount: "",
    paid_to: "",
    payment_mode: "cash",
    remarks: "",
  };

  const [formData, setFormData] = useState(emptyForm);

  /* =======================================================
     BULK
  ======================================================= */

  const createEmptyBulkRow = () => ({
    expense_date: getTodayDate(),
    category: "",
    amount: "",
    paid_to: "",
    payment_mode: "cash",
    remarks: "",
  });

  const [openBulkDialog, setOpenBulkDialog] = useState(false);

  const [bulkRows, setBulkRows] = useState([createEmptyBulkRow()]);

  /* =======================================================
     DELETE
  ======================================================= */

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     STYLES
  ======================================================= */

  const dialogPaperStyles = {
    backgroundColor: theme.palette.background.paper,
    color: textPrimary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark
      ? "0 20px 50px rgba(0,0,0,0.45)"
      : "0 20px 50px rgba(0,0,0,0.12)",
  };

    const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: inputBackground,
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: theme.palette.primary.main },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: textPrimary,
      opacity: 0.6,
    },
    "& .MuiInputBase-input::placeholder": {
      color: textPrimary,
      opacity: 0.75,
    },
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter: isDark ? "invert(1)" : "none",
      cursor: "pointer",
    },
    "& .MuiInputLabel-root": { color: textPrimary },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

    const selectStyles = {
    borderRadius: 2,
    backgroundColor: inputBackground,
    color: textPrimary,
    "& .MuiOutlinedInput-notchedOutline": { borderColor },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": { color: textPrimary },
    "& .MuiSelect-select": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "&.Mui-disabled .MuiSelect-select": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
      opacity: 0.6,
    },
  };

    const selectLabelStyles = {
    "& .MuiInputLabel-root": { color: textPrimary, opacity: 0.85 },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${theme.palette.divider}`,
        maxHeight: 300,
        "& .MuiMenuItem-root": {
          color: textPrimary,
          "&:hover": { backgroundColor: theme.palette.action.hover },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.16)"
                : "rgba(0,0,0,0.12)",
            },
          },
        },
      },
    },
  };

  /* =======================================================
     ERROR HELPERS
  ======================================================= */

  const formatFieldLabel = (field) => {
    const labels = {
      expense_date: "Expense Date",
      category: "Category",
      amount: "Amount",
      paid_to: "Paid To",
      payment_mode: "Payment Mode",
      remarks: "Remarks",
      organization: "Organization",
      branch: "Branch",
      expenses: "Expenses",
    };

    return (
      labels[field] ||
      field
        ?.replaceAll("_", " ")
        ?.replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  const getErrorMessage = (error) => {
    if (error?.response?.status === 401) {
      return "Unauthorized. Please login again.";
    }

    const backendErrors = error?.response?.data?.errors;

    if (
      backendErrors &&
      typeof backendErrors === "object" &&
      Object.keys(backendErrors).length > 0
    ) {
      return Object.entries(backendErrors)
        .map(([field, messages]) => {
          const message = Array.isArray(messages)
            ? messages.join(" ")
            : typeof messages === "string"
            ? messages
            : Object.values(messages)
                .flat(Infinity)
                .map((item) => String(item))
                .join(" ");

          return `${formatFieldLabel(field)}: ${message}`;
        })
        .join(" | ");
    }

    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }

    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    if (error?.response?.data?.error) {
      return error.response.data.error;
    }

    if (error?.response?.data) {
      const data = error.response.data;

      if (typeof data === "string") {
        return data;
      }

      return Object.values(data)
        .flat(Infinity)
        .map((item) => String(item))
        .join(" ");
    }

    return error?.message || "Something went wrong.";
  };

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const showSnackbar = useCallback(
    (message, severity = "success") => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* =======================================================
     READ LOGGED-IN USER (organization only — branch now
     comes from the global selector)
  ======================================================= */

  useEffect(() => {
    const user = getStoredUser();

    if (!user) {
      console.warn("Logged-in user not found.");
      return;
    }

    setCurrentUser(user);

    const orgId = user.organization_id || user.organization || "";

    setOrganizationId(orgId ? String(orgId) : "");
  }, []);

  /* =======================================================
     FETCH CATEGORIES
     GET /library/expense-category/list/
     Branch-scoped (same as the Expense Category page):
     params: organization (int), branch (int),
             is_active (boolean)
  ======================================================= */

  const fetchCategories = useCallback(
    async (orgOverride, branchOverride) => {
      const org = orgOverride ?? organizationId;

      const branch = branchOverride ?? selectedBranchId;

      if (!org) {
        setCategories([]);
        return;
      }

      setLoadingCategories(true);

      try {
        const params = {
          organization: Number(org),
          is_active: true,
        };

        if (branch !== "" && branch !== null && branch !== undefined) {
          params.branch = Number(branch);
        }

        const response = await api.get(CATEGORY_LIST_API, { params });

        const list = getArray(response);

        const normalizedCategories = list
          .map((item) => ({
            ...item,
            id: item.id ?? item.category_id ?? item.value,
            category_name:
              item.category_name || item.name || item.label || "",
          }))
          .filter(
            (item) =>
              item.id !== null &&
              item.id !== undefined &&
              item.id !== ""
          );

        setCategories(normalizedCategories);
      } catch (error) {
        console.error("Category List Error:", error);

        setCategories([]);

        showSnackbar(getErrorMessage(error), "error");
      } finally {
        setLoadingCategories(false);
      }
    },
    [organizationId, selectedBranchId, showSnackbar]
  );

  const fetchPaymentMethods = useCallback(async () => {
  setLoadingPaymentMethods(true);

  try {
    const response = await api.get(PAYMENT_METHOD_API);

    const list = getArray(response)
      .filter((item) => item.is_active !== false)
      .map((item) => ({
        id: item.id,
        name: item.name,
        // value sent to expense APIs (same format as before: cash/bank/upi)
        value: String(item.name || "").trim().toLowerCase(),
      }))
      .filter((item) => item.value);

    setPaymentMethods(list);
  } catch (error) {
    console.error("Payment Methods Error:", error);

    setPaymentMethods([]);

    showSnackbar(getErrorMessage(error), "error");
  } finally {
    setLoadingPaymentMethods(false);
  }
}, [showSnackbar]);

  /* =======================================================
     FETCH EXPENSES
     GET /library/expense/list/
     params: organization, branch, category, payment_mode,
             expense_date_from, expense_date_to, is_active
  ======================================================= */

  const fetchExpenses = useCallback(
    async (overrides = {}) => {
      if (!organizationId) return;

      setLoading(true);

      try {
        const params = {
          organization: Number(organizationId),
        };

        const branch = overrides.branch !== undefined
          ? overrides.branch
          : selectedBranchId;

        if (branch !== "" && branch !== null && branch !== undefined) {
          params.branch = Number(branch);
        }

        const category =
          overrides.category !== undefined
            ? overrides.category
            : filterCategory;

        if (category !== "") {
          params.category = Number(category);
        }

        const paymentMode =
          overrides.payment_mode !== undefined
            ? overrides.payment_mode
            : filterPaymentMode;

        if (paymentMode !== "") {
          params.payment_mode = paymentMode;
        }

        const dateFrom =
          overrides.date_from !== undefined
            ? overrides.date_from
            : filterDateFrom;

        if (dateFrom) {
          params.expense_date_from = dateFrom;
        }

        const dateTo =
          overrides.date_to !== undefined
            ? overrides.date_to
            : filterDateTo;

        if (dateTo) {
          params.expense_date_to = dateTo;
        }

        const response = await api.get(EXPENSE_LIST_API, { params });

        setExpenses(getArray(response));
      } catch (error) {
        console.error("Expense List Error:", error);

        setExpenses([]);

        showSnackbar(getErrorMessage(error), "error");
      } finally {
        setLoading(false);

        setInitialLoading(false);
      }
    },
    [
      organizationId,
      selectedBranchId,
      filterCategory,
      filterPaymentMode,
      filterDateFrom,
      filterDateTo,
      showSnackbar,
    ]
  );

  useEffect(() => {
  fetchPaymentMethods();
}, [fetchPaymentMethods]);

  /* Re-fetch whenever the global branch or organization
     changes (also runs on mount). */

  useEffect(() => {
    if (!organizationId) return;

    fetchExpenses();

    fetchCategories();
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     SEARCH — client-side filter
  ======================================================= */

  const filteredExpenses = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) return expenses;

    return expenses.filter((expense) => {
      const paidTo = String(expense.paid_to || expense.vendor_name || "").toLowerCase();

      const category = String(
        expense.category_name || expense.category || ""
      ).toLowerCase();

      const remarks = String(expense.remarks || "").toLowerCase();

      const paymentMode = String(expense.payment_mode || "").toLowerCase();

      const expenseId = String(expense.expense_id || "").toLowerCase();

      return (
        paidTo.includes(search) ||
        category.includes(search) ||
        remarks.includes(search) ||
        paymentMode.includes(search) ||
        expenseId.includes(search)
      );
    });
  }, [expenses, searchText]);

  const displayedExpenses = filteredExpenses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* =======================================================
     CATEGORY HELPERS
  ======================================================= */

  const getSelectedCategory = (categoryValue) => {
    return categories.find(
      (category) => String(category.id) === String(categoryValue)
    );
  };

  const getExpenseCategoryId = (expense) => {
    if (expense?.category && typeof expense.category === "object") {
      return expense.category.id ?? expense.category.category_id ?? "";
    }

    return expense?.category_id ?? expense?.category ?? "";
  };

  const getCategoryName = (expense) => {
    if (expense.category_name) return expense.category_name;

    if (expense.category?.name) return expense.category.name;

    if (expense.category?.category_name) {
      return expense.category.category_name;
    }

    const categoryId = getExpenseCategoryId(expense);

    const category = categories.find(
      (item) => String(item.id) === String(categoryId)
    );

    return category?.category_name || "-";
  };

  /* =======================================================
     OPEN CREATE / EDIT
  ======================================================= */

  const handleOpenCreate = () => {
    setEditMode(false);
    setSelectedExpenseId(null);

    setFormData({
      ...emptyForm,
      expense_date: getTodayDate(),
    });

    setOpenDialog(true);

    fetchCategories();
  };

  const handleOpenEdit = (expense) => {
    /* Business rule: approved expenses cannot be edited. */

    if (String(expense.status || "").toLowerCase() === "approved") {
      showSnackbar("Approved expenses cannot be edited.", "error");
      return;
    }

    setEditMode(true);
    setSelectedExpenseId(expense.id);

    const categoryId = getExpenseCategoryId(expense);

    setFormData({
      expense_date: expense.expense_date || getTodayDate(),
      category:
        categoryId !== null && categoryId !== undefined
          ? String(categoryId)
          : "",
      amount: expense.amount ?? "",
      paid_to: expense.paid_to || expense.vendor_name || "",
      payment_mode: String(expense.payment_mode || "cash").toLowerCase(),
      remarks: expense.remarks || "",
    });

    setOpenDialog(true);

    fetchCategories();
  };

  const handleCloseDialog = () => {
    if (saving) return;

    setOpenDialog(false);
    setSelectedExpenseId(null);
    setEditMode(false);
  };

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateExpense = (data) => {
    if (!data.expense_date) {
      showSnackbar("Please select expense date.", "error");
      return false;
    }

    if (!data.category) {
      showSnackbar("Please select expense category.", "error");
      return false;
    }

    if (!getSelectedCategory(data.category)) {
      showSnackbar(
        "Selected expense category is not available for this branch.",
        "error"
      );
      return false;
    }

    if (!data.amount || Number(data.amount) <= 0) {
      showSnackbar("Please enter a valid amount.", "error");
      return false;
    }

    if (!data.paid_to?.trim()) {
      showSnackbar("Please enter Paid To.", "error");
      return false;
    }

    if (!data.payment_mode) {
      showSnackbar("Please select payment mode.", "error");
      return false;
    }

    return true;
  };

  /* =======================================================
     BUILD PAYLOAD
     organization / branch / category as integers,
     amount as a decimal STRING (per Swagger).
  ======================================================= */

  const buildExpensePayload = (data) => {
    return {
      organization: Number(organizationId),
      branch: Number(selectedBranchId),
      expense_date: data.expense_date,
      category: Number(data.category),
      amount: String(data.amount),
      paid_to: data.paid_to.trim(),
      payment_mode: data.payment_mode,
      remarks: data.remarks?.trim() || null,
    };
  };

  /* =======================================================
     CREATE — POST /library/expense/create/
  ======================================================= */

  const handleCreate = async () => {
    if (!organizationId) {
      showSnackbar("Organization is required.", "error");
      return;
    }

    if (!selectedBranchId) {
      showSnackbar(
        "Branch is required. Please select a branch from the global branch selector.",
        "error"
      );
      return;
    }

    if (!validateExpense(formData)) return;

    setSaving(true);

    try {
      const payload = buildExpensePayload(formData);

      const response = await api.post(EXPENSE_CREATE_API, payload);

      showSnackbar(
        response.data?.message || "Expense created successfully.",
        "success"
      );

      setOpenDialog(false);

      setFormData({ ...emptyForm, expense_date: getTodayDate() });

      await fetchExpenses();
    } catch (error) {
      console.error("Create Expense Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     UPDATE — PUT /library/expense/update/{id}/
  ======================================================= */

  const handleUpdate = async () => {
    if (!selectedExpenseId) return;

    if (!selectedBranchId) {
      showSnackbar(
        "Branch is required. Please select a branch from the global branch selector.",
        "error"
      );
      return;
    }

    if (!validateExpense(formData)) return;

    setSaving(true);

    try {
      const payload = buildExpensePayload(formData);

      const response = await api.put(
        `${EXPENSE_UPDATE_API}${selectedExpenseId}/`,
        payload
      );

      showSnackbar(
        response.data?.message || "Expense updated successfully.",
        "success"
      );

      setOpenDialog(false);

      await fetchExpenses();
    } catch (error) {
      console.error("Update Expense Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (editMode) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  /* =======================================================
     BULK ROWS
  ======================================================= */

  const handleBulkChange = (index, field, value) => {
    setBulkRows((prev) => {
      const updated = [...prev];

      updated[index] = { ...updated[index], [field]: value };

      return updated;
    });
  };

  const addBulkRow = () => {
    setBulkRows((prev) => [...prev, createEmptyBulkRow()]);
  };

  const removeBulkRow = (index) => {
    if (bulkRows.length === 1) return;

    setBulkRows((prev) => prev.filter((_, i) => i !== index));
  };

  /* =======================================================
     BULK CREATE — POST /library/expense/bulk-create/
     { expenses: [{ organization: int, branch: int, ... }] }
  ======================================================= */

  const handleBulkCreate = async () => {
    if (!organizationId) {
      showSnackbar("Organization is required.", "error");
      return;
    }

    if (!selectedBranchId) {
      showSnackbar(
        "Branch is required. Please select a branch from the global branch selector.",
        "error"
      );
      return;
    }

    const invalidRow = bulkRows.some(
      (row) =>
        !row.expense_date ||
        !row.category ||
        !row.amount ||
        Number(row.amount) <= 0 ||
        !row.paid_to?.trim() ||
        !row.payment_mode
    );

    if (invalidRow) {
      showSnackbar(
        "Please complete all required fields in every expense row.",
        "error"
      );
      return;
    }

    const invalidCategory = bulkRows.some(
      (row) => !getSelectedCategory(row.category)
    );

    if (invalidCategory) {
      showSnackbar(
        "One or more selected expense categories are not available for this branch.",
        "error"
      );
      return;
    }

    setSaving(true);

    try {
      const payload = {
        expenses: bulkRows.map((row) => ({
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
          expense_date: row.expense_date,
          category: Number(row.category),
          amount: String(row.amount),
          paid_to: row.paid_to.trim(),
          payment_mode: row.payment_mode,
          remarks: row.remarks?.trim() || null,
        })),
      };

      const response = await api.post(EXPENSE_BULK_CREATE_API, payload);

      showSnackbar(
        response.data?.message || "Expenses created successfully.",
        "success"
      );

      setOpenBulkDialog(false);

      setBulkRows([createEmptyBulkRow()]);

      await fetchExpenses();
    } catch (error) {
      console.error("Bulk Expense Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     DELETE (soft) — DELETE /library/expense/delete/{id}/
  ======================================================= */

  const handleOpenDelete = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    if (saving) return;

    setDeleteId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setSaving(true);

    try {
      await api.delete(`${EXPENSE_DELETE_API}${deleteId}/`);

      showSnackbar("Expense deleted successfully.", "success");

      setOpenDeleteDialog(false);
      setDeleteId(null);

      await fetchExpenses();
    } catch (error) {
      console.error("Delete Expense Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     FILTERS
  ======================================================= */

  const handleApplyFilter = () => {
    setPage(0);

    fetchExpenses();

    setFilterModalOpen(false);
  };

  const handleResetFilter = () => {
    setFilterCategory("");
    setFilterPaymentMode("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSearchText("");
    setPage(0);

    fetchExpenses({
      category: "",
      payment_mode: "",
      date_from: "",
      date_to: "",
    });

    setFilterModalOpen(false);
  };

  const handleRefresh = () => {
    fetchExpenses();
fetchPaymentMethods(); 
    fetchCategories();
  };

  const handleClearSearch = () => {
    setSearchText("");
    setPage(0);
  };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /* =======================================================
     DISPLAY HELPERS
  ======================================================= */

  const currentBranchName =
    expenses.find(
      (item) => String(item.branch) === String(selectedBranchId)
    )?.branch_name ||
    currentUser?.branch_name ||
    (selectedBranchId ? `Branch ${selectedBranchId}` : "-");

  const formatAmount = (amount) => {
    const value = Number(amount);

    if (Number.isNaN(value)) return amount || "₹0";

    return `₹${value.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPaymentMode = (mode) => {
    if (!mode) return "-";

    return mode.charAt(0).toUpperCase() + mode.slice(1);
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: textPrimary,
      }}
    >
      {/* ===================================================
          PAGE HEADER
      =================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", lg: "center" },
          gap: 1.5,
          mb: 3,
          flexWrap: { xs: "wrap", lg: "nowrap" },
          width: "100%",
        }}
      >
        {/* SEARCH — client-side, live filtering */}

        <TextField
          size="small"
          variant="outlined"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            setPage(0);
          }}
          placeholder="Search expense ID, paid to, category..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
            endAdornment: (
              searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ color: textPrimary }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            ),
          }}
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 340px", lg: "1 1 auto" },
            minWidth: { lg: 280 },
            maxWidth: { lg: 380 },
            "& .MuiOutlinedInput-root": {
              height: 50,
              borderRadius: "30px",
              backgroundColor: inputBackground,
              color: textPrimary,
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textPrimary,
                opacity: 0.75,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(0,0,0,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* ACTIONS */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flexShrink: 0,
            width: { xs: "100%", lg: "auto" },
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            ml: { lg: "auto" },
          }}
        >
          {/* REFRESH */}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Refresh
          </Button>

          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Filters
          </Button>

          {/* BULK ADD */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setBulkRows([createEmptyBulkRow()]);
              setOpenBulkDialog(true);
              fetchCategories();
            }}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Bulk Add
          </Button>

          {/* ADD EXPENSE */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Add Expense
          </Button>
        </Box>
      </Box>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          <CustomTableScrollbar
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Table stickyHeader sx={{ minWidth: 1150 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      backgroundColor: theme.palette.primary.main,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      borderBottom: "none",
                      py: 1.8,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>

                  <TableCell>Expense ID</TableCell>

                  <TableCell>Date</TableCell>

                  <TableCell>Category</TableCell>

                  <TableCell>Paid To</TableCell>

                  <TableCell>Amount</TableCell>

                  <TableCell>Payment Mode</TableCell>

                  <TableCell>Branch</TableCell>

                  <TableCell>Status</TableCell>

                  <TableCell>Remarks</TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading || initialLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      align="center"
                      sx={{ py: 7, borderBottom: "none" }}
                    >
                      <CircularProgress
                        size={32}
                        sx={{ color: theme.palette.primary.main }}
                      />

                      <Typography sx={{ mt: 1.5, color: textPrimary }}>
                        Loading expenses...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayedExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      align="center"
                      sx={{ py: 8, borderBottom: "none" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: textPrimary,
                        }}
                      >
                        No expenses found
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ mt: 0.7, color: textPrimary }}
                      >
                        Try changing your search or filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedExpenses.map((expense, index) => (
                    <TableRow key={expense.id || index} hover>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {/* EXPENSE ID */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography
                          fontWeight={600}
                          sx={{ color: textPrimary }}
                        >
                          {expense.expense_id || "-"}
                        </Typography>
                      </TableCell>

                      {/* DATE */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography sx={{ color: textPrimary }}>
                          {expense.expense_date || "-"}
                        </Typography>
                      </TableCell>

                      {/* CATEGORY */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography
                          fontWeight={700}
                          sx={{ color: textPrimary }}
                        >
                          {getCategoryName(expense)}
                        </Typography>
                      </TableCell>

                      {/* PAID TO */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography sx={{ color: textPrimary }}>
                          {expense.paid_to || expense.vendor_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* AMOUNT */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography
                          fontWeight={800}
                          sx={{ color: theme.palette.success.main }}
                        >
                          {formatAmount(expense.amount)}
                        </Typography>
                      </TableCell>

                      {/* PAYMENT MODE */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Chip
                          size="small"
                          label={formatPaymentMode(expense.payment_mode)}
                          sx={{
                            height: 26,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>

                      {/* BRANCH */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography sx={{ color: textPrimary }}>
                          {expense.branch_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* STATUS */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Chip
                          size="small"
                          label={
                            expense.status_display || expense.status || "-"
                          }
                          color={
                            String(expense.status || "").toLowerCase() ===
                            "approved"
                              ? "success"
                              : String(expense.status || "").toLowerCase() ===
                                "rejected"
                              ? "error"
                              : "warning"
                          }
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            borderRadius: "7px",
                          }}
                        />
                      </TableCell>

                      {/* REMARKS */}

                      <TableCell sx={{ py: 1.5, px: 2, maxWidth: 200 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: textPrimary,
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {expense.remarks || "-"}
                        </Typography>
                      </TableCell>

                      {/* ACTIONS */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          {/* EDIT — disabled for approved expenses */}

                          {String(expense.status || "").toLowerCase() ===
                          "approved" ? (
                            <Tooltip title="Approved expenses cannot be edited">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled
                                  sx={{
                                    color: theme.palette.action.disabled,
                                    border: `1px solid ${alpha(
                                      theme.palette.action.disabled,
                                      0.4
                                    )}`,
                                    borderRadius: 1.5,
                                    cursor: "not-allowed",
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(expense)}
                                sx={{
                                  color: theme.palette.primary.main,
                                  border: `1px solid ${alpha(
                                    theme.palette.primary.main,
                                    0.3
                                  )}`,
                                  borderRadius: 1.5,
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      theme.palette.primary.main,
                                      isDark ? 0.15 : 0.08
                                    ),
                                  },
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* DELETE */}

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(expense.id)}
                              sx={{
                                color: theme.palette.error.main,
                                border: `1px solid ${alpha(
                                  theme.palette.error.main,
                                  0.3
                                )}`,
                                borderRadius: 1.5,
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.error.main,
                                    isDark ? 0.15 : 0.08
                                  ),
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CustomTableScrollbar>

          {/* PAGINATION — client-side */}

          <TablePagination
            component="div"
            count={filteredExpenses.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,
              borderTop: `1px solid ${borderColor}`,
              "& .MuiTablePagination-selectIcon": {
                color: textPrimary,
              },
              "& .MuiTablePagination-actions button": {
                color: textPrimary,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* ===================================================
          FILTER DIALOG
      =================================================== */}

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Filter Expenses
          </Typography>

          <IconButton
            onClick={() => setFilterModalOpen(false)}
            sx={{ color: textPrimary }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mt: 1,
            }}
          >
            {/* BRANCH — from global selector, read-only */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                BRANCH
              </Typography>

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: textPrimary }}
                >
                  {currentBranchName}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{ mt: 0.5, display: "block", color: textPrimary }}
              >
                Change the branch from the global branch selector in the
                layout.
              </Typography>
            </Box>

            {/* CATEGORY */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                CATEGORY
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={filterCategory}
                  onChange={(event) => setFilterCategory(event.target.value)}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">All Categories</MenuItem>

                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.category_name || `Category ${category.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* PAYMENT MODE */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                PAYMENT MODE
              </Typography>

              <FormControl fullWidth size="small">
              <Select
  value={filterPaymentMode}
  onChange={(event) => setFilterPaymentMode(event.target.value)}
  displayEmpty
  disabled={loadingPaymentMethods}
  MenuProps={menuProps}
  sx={selectStyles}
>
  <MenuItem value="">All Payment Modes</MenuItem>

  {paymentMethods.map((method) => (
    <MenuItem key={method.id} value={method.value}>
      {method.name}
    </MenuItem>
  ))}
</Select>
              </FormControl>
            </Box>

            {/* DATE RANGE — maps to expense_date_from / expense_date_to */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label="Date From"
                type="date"
                size="small"
                value={filterDateFrom}
                onChange={(event) => setFilterDateFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />

              <TextField
                label="Date To"
                type="date"
                size="small"
                value={filterDateTo}
                onChange={(event) => setFilterDateTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleResetFilter}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: textPrimary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilter}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          ADD / EDIT DIALOG
      =================================================== */}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {editMode ? "Edit Expense" : "Add Expense"}
          </Typography>

          <IconButton
            size="small"
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              color: textPrimary,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 2,
              py: 2,
            }}
          >
          
            {/* EXPENSE DATE */}

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Expense Date"
              name="expense_date"
              value={formData.expense_date}
              onChange={handleFormChange}
              InputLabelProps={{ shrink: true }}
              required
              sx={textFieldStyles}
            />

            {/* CATEGORY */}

            <FormControl
              fullWidth
              size="small"
              required
             sx={{ my: "auto", ...selectLabelStyles }}
            >
              <InputLabel>Expense Category</InputLabel>

              <Select
                name="category"
                value={formData.category}
                label="Expense Category"
                onChange={handleFormChange}
                disabled={loadingCategories}
                MenuProps={menuProps}
                sx={selectStyles}
              >
                <MenuItem value="">
                  {loadingCategories ? "Loading..." : "Select Category"}
                </MenuItem>

                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.category_name || `Category ${category.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* PAID TO */}

            <TextField
              fullWidth
              size="small"
              label="Paid To"
              name="paid_to"
              value={formData.paid_to}
              onChange={handleFormChange}
              placeholder="Person / Vendor"
              required
              sx={textFieldStyles}
            />

            {/* AMOUNT — sent as a decimal string */}

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Amount"
              name="amount"
              value={formData.amount}
              onChange={handleFormChange}
              inputProps={{ min: 0.01, step: 0.01 }}
              required
              sx={textFieldStyles}
            />

            {/* PAYMENT MODE */}

            <FormControl fullWidth size="small" required sx={{ my: "auto", ...selectLabelStyles }}>
              <InputLabel>Payment Mode</InputLabel>

           <Select
  name="payment_mode"
  value={formData.payment_mode}
  label="Payment Mode"
  onChange={handleFormChange}
  disabled={loadingPaymentMethods}
  MenuProps={menuProps}
  sx={selectStyles}
>
  {paymentMethods.map((method) => (
    <MenuItem key={method.id} value={method.value}>
      {method.name}
    </MenuItem>
  ))}
</Select>
            </FormControl>

            {/* REMARKS */}

            <TextField
              fullWidth
              size="small"
              label="Remarks"
              name="remarks"
              value={formData.remarks}
              onChange={handleFormChange}
              placeholder="Additional remarks"
              sx={{
                ...textFieldStyles,
                gridColumn: { xs: "span 1", sm: "span 2" },
              }}
            />
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: textPrimary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save fontSize="small" />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{
              minWidth: 130,
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              "& .MuiSvgIcon-root": { color: "inherit" },
            }}
          >
            {saving
              ? "Saving..."
              : editMode
              ? "Update Expense"
              : "Save Expense"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          BULK CREATE DIALOG
      =================================================== */}

      <Dialog
        open={openBulkDialog}
        onClose={() => {
          if (!saving) setOpenBulkDialog(false);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Bulk Create Expenses
          </Typography>

          <IconButton
            size="small"
            onClick={() => setOpenBulkDialog(false)}
            disabled={saving}
            sx={{
              color: textPrimary,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, py: 2 }}>


            {/* HEADER + COUNT */}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: textPrimary }}>
                Expense Entries
              </Typography>

              <Chip
                label={`${bulkRows.length} ${
                  bulkRows.length === 1 ? "Entry" : "Entries"
                }`}
                size="small"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}15`,
                }}
              />
            </Box>

            {/* ROWS — scrollable via CustomTableScrollbar */}

            <box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxHeight: 420,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {bulkRows.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2.5,
                    p: 2.5,
                    mb: 1,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  {/* ROW HEADER */}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: theme.palette.primary.main,
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Typography sx={{ fontWeight: 700, color: textPrimary }}>
                        Expense {index + 1}
                      </Typography>
                    </Box>

                    {bulkRows.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeBulkRow(index)}
                        disabled={saving}
                        sx={{
                          color: theme.palette.error.main,
                          backgroundColor: `${theme.palette.error.main}12`,
                          "&:hover": {
                            backgroundColor: `${theme.palette.error.main}20`,
                          },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {/* FIELDS */}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                      },
                      gap: 2,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Expense Date"
                      value={row.expense_date}
                      onChange={(event) =>
                        handleBulkChange(
                          index,
                          "expense_date",
                          event.target.value
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={textFieldStyles}
                    />

                    <FormControl
                      fullWidth
                      size="small"
                      required
             sx={{ my: "auto", ...selectLabelStyles }}
                    >
                      <InputLabel>Expense Category</InputLabel>

                      <Select
                        value={row.category}
                        label="Expense Category"
                        onChange={(event) =>
                          handleBulkChange(
                            index,
                            "category",
                            event.target.value
                          )
                        }
                        disabled={loadingCategories}
                        MenuProps={menuProps}
                        sx={selectStyles}
                      >
                        <MenuItem value="">
                          {loadingCategories ? "Loading..." : "Select Category"}
                        </MenuItem>

                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.category_name ||
                              `Category ${category.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      size="small"
                      label="Paid To"
                      placeholder="Person / Vendor"
                      value={row.paid_to}
                      onChange={(event) =>
                        handleBulkChange(index, "paid_to", event.target.value)
                      }
                      sx={textFieldStyles}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Amount"
                      value={row.amount}
                      onChange={(event) =>
                        handleBulkChange(index, "amount", event.target.value)
                      }
                      inputProps={{ min: 0.01, step: 0.01 }}
                      sx={textFieldStyles}
                    />

                    <FormControl fullWidth size="small" sx={{ my: "auto", ...selectLabelStyles }}>
                      <InputLabel>Payment Mode</InputLabel>

                    <Select
  value={row.payment_mode}
  label="Payment Mode"
  onChange={(event) =>
    handleBulkChange(index, "payment_mode", event.target.value)
  }
  disabled={loadingPaymentMethods}
  MenuProps={menuProps}
  sx={selectStyles}
>
  {paymentMethods.map((method) => (
    <MenuItem key={method.id} value={method.value}>
      {method.name}
    </MenuItem>
  ))}
</Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      size="small"
                      label="Remarks"
                      placeholder="Additional remarks"
                      value={row.remarks}
                      onChange={(event) =>
                        handleBulkChange(index, "remarks", event.target.value)
                      }
                      sx={{
                        ...textFieldStyles,
                        gridColumn: {
                          xs: "span 1",
                          sm: "span 2",
                          md: "span 2",
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </box>

          
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
              {/* ADD ANOTHER */}

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={addBulkRow}
              disabled={saving}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                px: 2.5,
                borderStyle: "dashed",
                color: "inherit",
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderStyle: "dashed",
                  backgroundColor: `${theme.palette.primary.main}10`,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              Add Another Expense
            </Button>

            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Save />
                )
              }
              onClick={handleBulkCreate}
              disabled={saving}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                fontWeight: 600,
                "& .MuiSvgIcon-root": { color: "inherit" },
              }}
            >
              {saving ? "Saving..." : "Create Expenses"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          DELETE DIALOG
      =================================================== */}

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Expense</DialogTitle>

        <Divider />

        <StyledDialogContent sx={{ py: 3 }}>
          <Typography sx={{ color: textPrimary }}>
            Are you sure you want to delete this expense?
          </Typography>

          <Typography variant="body2" sx={{ mt: 1, color: textSecondary }}>
            The expense will be soft deleted and will not be permanently
            removed.
          </Typography>
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleCloseDelete}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: textPrimary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Delete />
              )
            }
            onClick={handleDelete}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ExpenseEntry;